import type { WoocommerceAddCartItemRequest, WoocommerceApplyCouponRequest, WoocommerceCartResponse, WoocommerceCartResponse_Item, WoocommerceCheckoutFields, WoocommerceRemoveCartItemRequest, WoocommerceRemoveCouponRequest, WoocommerceSelectShippingRateRequest, WoocommerceUpdateCartItemRequest, WoocommerceUpdateCustomerRequest } from "@/types/woocommerce.types";
import {apiFetch} from "@/libs/wordpress.facade";
import { computed, reactive, readonly, ref, shallowRef, type Ref } from "vue";
import type { APIFetchOptions } from "@wordpress/api-fetch";

const cart = shallowRef<WoocommerceCartResponse|undefined>();
const cartNonce = ref<string|null|undefined>(sessionStorage.getItem('woo-cart-nonce'));
const cartToken = ref<string|null|undefined>(sessionStorage.getItem('woo-cart-token'));
const checkoutFields = shallowRef<WoocommerceCheckoutFields|undefined>();
const requesting = ref<false|string>(false);

function isCartItemStonex(cartItem?: WoocommerceCartResponse_Item){
	return cartItem?.extensions.itlp.product_type == 'stonex';
}

const hasStonexProducts = computed(() => {
	if(!cart.value) return false;

	return Boolean(cart.value.items.find(isCartItemStonex));
});

const hasStonexShipping = computed(() => {
	if(!cart.value || !cart.value.shipping_rates.length) return false;

	return Boolean(cart.value.shipping_rates.find(p => p.package_id == 'stonex'));
});

const selectedStonexShippingRate = computed(() => {
	if(!cart.value || !cart.value.shipping_rates.length) return undefined;
	const stonexRate = cart.value?.shipping_rates.find(rates => rates.package_id == 'stonex');
	return stonexRate?.shipping_rates.find(rate => rate.selected);
});

/**
 * The selected shipping rate for not stonex products
 */
const selectedShippingRate = computed(() => {
	if(!cart.value || !cart.value.shipping_rates.length) return undefined;
	const itlpRate = cart.value?.shipping_rates.find(rates => rates.package_id != 'stonex');
	return itlpRate?.shipping_rates.find(rate => rate.selected);
});

const showShippingAddressStep = computed(() => {
	return hasStonexShipping.value || !['italdeposito_shipping', 'italpreziosi_local_pickup'].includes(selectedShippingRate.value?.method_id as string);
});

const showShippingMethodStep = computed(() => {
	if(selectedShippingRate.value?.method_id === 'italdeposito_shipping') return false;
	if(!cart.value || !cart.value.shipping_rates.length) return false;
	const notStonexRate = cart.value?.shipping_rates.find(rates => rates.package_id != 'stonex');
	if(!notStonexRate) return false;
	return notStonexRate.shipping_rates.filter(rate => rate.method_id != 'italdeposito_shipping').length > 1;
});

function isStoreApiRequest(options: APIFetchOptions){
	const url = options.url || options.path;
	if(!url) return false;
	return /wc\/store\/v1\//.exec(url) !== null;
}

function isStoreApiCheckoutRequest(options: APIFetchOptions){
	const url = options.url || options.path;
	if(!url) return false;
	return /wc\/store\/v1\/checkout/.exec(url) !== null;
}

function needAppendStoreApiHeaders(options: APIFetchOptions){
	const url = options.url || options.path;
	if(!url || !options.method || options.method === 'GET') return false;
	return /wc\/store\/v1\//.exec(url) !== null;
}

function handleResponse(res: Response, options: APIFetchOptions){
	if(typeof(res) == 'object' && res.headers && !options.parse && isStoreApiRequest(options)){
		cartNonce.value = res.headers.get('nonce');
		cartToken.value = res.headers.get('cart-token');

		// @ts-ignore
		if(typeof(apiFetch['setNonce']) === 'function') apiFetch['setNonce'](res.headers);
		// @ts-ignore
		if(typeof(apiFetch['setCartHash']) === 'function') apiFetch['setCartHash'](res.headers);
	}
}

apiFetch.use(async (options, next) => {
	if(!options.headers) options.headers = {};
	// @ts-ignore
	if(typeof(apiFetch['setNonce']) !== 'function' && cartNonce.value && !options.headers['nonce']) options.headers['nonce'] = cartNonce.value;
	if(window.itlp_env == 'local' && cartToken.value && !options.headers['Cart-Token']) options.headers['Cart-Token'] = cartToken.value;
	if((!options.method || options.method == 'GET') && isStoreApiCheckoutRequest(options) && !options.headers['nonce'] && !options.headers['Nonce'] && cartNonce.value) options.headers['nonce'] = cartNonce.value;

	try{
		const res = await next(options);

		handleResponse(res, options);
		
		return res;
	}catch(e){
		handleResponse(e as Response, options)
		throw e;
	}
});

function useWooCartEndpoint<T>(cartResource: string, method?: 'GET'|'POST'): [(data: T) => Promise<unknown>, Ref, Ref]{
	let abortController: AbortController;

	const error = shallowRef<unknown>();
	const errorBodyResponse = shallowRef<object|undefined>();

	async function call(data: T){
		error.value = undefined;
		errorBodyResponse.value = undefined;
		if(abortController) abortController.abort();
		abortController = new AbortController()
		const abortSignal = abortController.signal;

		if(requesting.value != 'paying') requesting.value = cartResource;
		try{
			const res = (await apiFetch({
				path: '/wc/store/v1/cart'+cartResource+'?_locale=site',
				method,
				data,
				signal: abortSignal,
				cache: 'no-store',
				parse: false
			})) as Response;

			cart.value = await res.json();
			checkoutFields.value = cart.value?.extensions.itlp?.checkout_fields;
		}catch(e){
			if(abortSignal.aborted) return 'aborted';
			error.value = e;
			if(e && e instanceof Response) errorBodyResponse.value = await e.json();
		}
		if(requesting.value == cartResource) requesting.value = false;

		return error.value;
	};

	return [call, error, errorBodyResponse];
}

const [refreshCart, cartError] = useWooCartEndpoint<void>('');
const [updateCustomer, updateCustomerError, updateCustomerBodyError] = useWooCartEndpoint<WoocommerceUpdateCustomerRequest>('/update-customer', 'POST');
const [addCartItem, addCartItemError] = useWooCartEndpoint<WoocommerceAddCartItemRequest>('/add-item', 'POST');
const [removeCartItem, removeItemError] = useWooCartEndpoint<WoocommerceRemoveCartItemRequest>('/remove-item', 'POST');
const [updateCartItem, updateCartItemError] = useWooCartEndpoint<WoocommerceUpdateCartItemRequest>('/update-item', 'POST');
const [applyCoupon, applyCouponError] = useWooCartEndpoint<WoocommerceApplyCouponRequest>('/apply-coupon', 'POST');
const [removeCoupon, removeCouponError] = useWooCartEndpoint<WoocommerceRemoveCouponRequest>('/remove-coupon', 'POST');
const [selectShippingRate, selectShippingRateError] = useWooCartEndpoint<WoocommerceSelectShippingRateRequest>('/select-shipping-rate', 'POST');


function updateCartNonce(newVal: string|undefined|null){
	cartNonce.value = newVal;
}

function clearErrors(){
	updateCustomerError.value = undefined;
	cartError.value = undefined;
	addCartItemError.value = undefined;
	removeItemError.value = undefined;
	updateCartItemError.value = undefined;
	applyCouponError.value = undefined;
	removeCouponError.value = undefined;
	selectShippingRateError.value = undefined;
}

function clearAll(){
	cart.value = undefined;
	checkoutFields.value = undefined;
	cartNonce.value = undefined;
	cartToken.value = undefined;
	clearErrors();
}

// get value from server?
const maxTimerDurationInMillis = 600000; // 10 min

function calcExpirationPercentage(currentMillis: number|null){
	if(!currentMillis || typeof(currentMillis) !== 'number') return 0;
	return Math.max(0, Math.min(((maxTimerDurationInMillis - currentMillis) * 100)/maxTimerDurationInMillis, 100));
}

function calcExpirationInMillis(){
	if(!cart.value?.extensions?.itlp?.quotation_expire_at) return null;
	const expireAt = cart.value.extensions.itlp.quotation_expire_at;
	if(typeof(expireAt) !== 'number') return null;
	return (expireAt * 1000) - Date.now();
}

function millisToMinutesAndSeconds(millis: number|null) {
	if(!millis || millis < 0) return {minutes: '00', seconds: '00', terminated: true};
	const minutes = Math.max(0, Math.floor(millis / 60000));
	const seconds = Math.max(0, Number(((millis % 60000) / 1000).toFixed(0)));
	return {
		minutes: (minutes < 10 ? '0' : '') + minutes, 
		seconds: (seconds < 10 ? '0' : '') + seconds,
		terminated: minutes == 0 && seconds == 0
	};
}

const expirationCountdown = ref<{minutes: string, seconds: string}>({minutes: '', seconds: ''});
const expirationPercentage = ref(0);
let lastExpirationUpdatedAt: number;
setInterval(() => {
	const millis = calcExpirationInMillis();
	expirationCountdown.value = millisToMinutesAndSeconds(millis);
	expirationPercentage.value = calcExpirationPercentage(millis);
	if(typeof(millis) == 'number' && millis < 0 && (!lastExpirationUpdatedAt || (Date.now() - lastExpirationUpdatedAt) > 30000)){
		lastExpirationUpdatedAt = Date.now();
		apiFetch({
			path: '/itlp/v1/refresh-cart',
			method: 'POST',
			parse: true,
			headers: cartNonce.value ? {
				'Nonce': cartNonce.value
			} : undefined
		}).then(() => {
			return refreshCart();
		}).catch(e => {
			// go to cart if 403
			if(e?.data?.status === 403){
				if(window.itlp_checkout_params?.cart_url) window.location.href = window.itlp_checkout_params?.cart_url;
				else window.location.href = '/';
			}
		});
	}
}, 1000);

export default function (){

	return {
		cart: readonly(cart),
		checkoutFields: readonly(checkoutFields),
		cartNonce: readonly(cartNonce),
		cartToken: readonly(cartToken),
		requesting,
		expirationCountdown,
		expirationPercentage,
		hasStonexShipping,
		hasStonexProducts,
		selectedShippingRate,
		selectedStonexShippingRate,
		showShippingAddressStep,
		showShippingMethodStep,
		isRequesting: readonly(requesting),
		updateCartNonce,
		updateCustomer, updateCustomerError, updateCustomerBodyError,
		refreshCart, cartError,
		addCartItem, addCartItemError,
		removeCartItem, removeItemError,
		updateCartItem, updateCartItemError,
		applyCoupon, applyCouponError,
		removeCoupon, removeCouponError,
		selectShippingRate, selectShippingRateError,
		clearAll,
		clearErrors
	}
}